import type {CompRef, PS} from '@wix/document-services-types'
import {registerHookWithPs} from '../hooks/eventHooks/eventHooks'
import {
    DATA_MODEL_HOOKS,
    COMPONENTS_HOOKS,
    COMPONENTS_METADATA_HOOKS,
    type ComponentDuplicateEvent,
    type DeriveComponentMetadataEvent
} from '@wix/document-manager-extensions/src/hooks'
import type {HooksExtensionApi, PlatformExtensionAPI} from '@wix/document-manager-extensions'
import repeaterHooks from '../hooks/componentHooks/repeaterHooks'
import {upgradeFacebookShareUrlOnAdditionFromExt} from '../hooks/componentHooks/facebookShare'
import {upgradePinterestPinUrlOnAdditionFromExt} from '../hooks/componentHooks/pinterestPinIt'
import {upgradeVKShareButtonUrlOnAdditionFromExt} from '../hooks/componentHooks/vkShareButton'
import {upgradeFacebookCommentsOnAdditionFromExt} from '../hooks/componentHooks/wFacebookComment'
import {upgradeWFacebookLinkOnAdditionFromExt} from '../hooks/componentHooks/wFacebookLike'

import tpaHooks from '../hooks/componentHooks/tpaHooks'
import svgShapeToVectorImageHook from '../hooks/componentHooks/svgShapeToVectorImageHook'
import verticalMenu from '../hooks/componentHooks/verticalMenu'
import matrixGallery from '../hooks/componentHooks/matrixGallery'

import fiveGridLineHooks from '../hooks/componentHooks/fiveGridLineHooks'
import {
    convertCompToNewSiteWidthHook,
    rconvertCompToNewSiteWidthHookFromExt
} from '../hooks/layoutHooks/siteWidthMigration'
import popupPageHooks from '../hooks/componentHooks/popupPageHooks'
import hooks from '../hooks/hooks'
import columns from '../hooks/componentHooks/columns'
import component from './component'
import componentsMetaData from '../componentsMetaData/componentsMetaData'

const bridgeDataUpdateBeforeToExtension = (_ps: PS, componentPointer: CompRef, dataItem: any) => {
    const {hooks: hooksApi} = _ps.extensionAPI as HooksExtensionApi
    hooksApi.executeHookAndUpdateValue(
        DATA_MODEL_HOOKS.DATA.UPDATE_BEFORE.createEvent({
            componentPointer,
            dataItem
        }),
        dataItem
    )
}
import type {ComponentBeforeAddDataEvent} from '@wix/document-manager-extensions/src/extensions/components/hooks'

const registerComponentHooksFromExt = (ps: PS) => {
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        upgradeFacebookShareUrlOnAdditionFromExt,
        'wysiwyg.viewer.components.FacebookShare'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        upgradeFacebookCommentsOnAdditionFromExt,
        'wysiwyg.viewer.components.WFacebookComment'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        upgradeWFacebookLinkOnAdditionFromExt,
        'wysiwyg.viewer.components.WFacebookLike'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        fiveGridLineHooks.replaceFullScreenPropertyWithDockingFromExt,
        'wysiwyg.viewer.components.FiveGridLine'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        upgradePinterestPinUrlOnAdditionFromExt,
        'wysiwyg.common.components.pinterestpinit.viewer.PinterestPinIt'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        upgradeVKShareButtonUrlOnAdditionFromExt,
        'wysiwyg.viewer.components.VKShareButton'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.AFTER_FROM_EXT.id,
        tpaHooks.addedGluedWidgetFromExt,
        'wysiwyg.viewer.components.tpapps.TPAGluedWidget'
    )
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_COMPONENT.ADJUST_COMP_LAYOUT.id,
        popupPageHooks.adjustCompLayoutInPopupPageFromExt,
        'mobile.core.components.Page'
    )

    registerHookWithPs(ps, COMPONENTS_HOOKS.ADD_ROOT.AFTER_FROM_EXT.id, rconvertCompToNewSiteWidthHookFromExt)
    registerHookWithPs(
        ps,
        COMPONENTS_HOOKS.ADD_ROOT.BEFORE_FROM_EXT.id,
        columns.handleSplitToColumnsFromExt,
        'wysiwyg.viewer.components.Column'
    )

    const {hooks: extensionHooksApi} = ps.extensionAPI as HooksExtensionApi
    extensionHooksApi.registerHook(
        COMPONENTS_HOOKS.ADD_COMPONENT.BEFORE.id,
        (event: ComponentBeforeAddDataEvent) => svgShapeToVectorImageHook.beforeAdd(ps, event),
        'wysiwyg.viewer.components.svgshape.SvgShape'
    )
    extensionHooksApi.registerHook(
        COMPONENTS_HOOKS.ADD_COMPONENT.BEFORE.id,
        (event: ComponentBeforeAddDataEvent) => verticalMenu.beforeAdd(ps, event),
        'wysiwyg.common.components.verticalmenu.viewer.VerticalMenu'
    )
    extensionHooksApi.registerHook(
        COMPONENTS_HOOKS.ADD_COMPONENT.BEFORE.id,
        (event: ComponentBeforeAddDataEvent) => matrixGallery.beforeAdd(ps, event),
        'wysiwyg.viewer.components.MatrixGallery'
    )
}
const syncBuilderComponentsMetadata = (ps: PS) => {
    const {platform} = ps.extensionAPI as PlatformExtensionAPI
    const builderCompTypesToReferredTypesMap = platform.getBuilderCompTypesToReferredTypesMap()
    Object.entries(builderCompTypesToReferredTypesMap).forEach(([componentType, referredType]) => {
        componentsMetaData.deriveMetaDataFrom(componentType, referredType)
    })
}

function initialize(ps: PS) {
    registerHookWithPs(
        ps,
        DATA_MODEL_HOOKS.GET_REPEATED_ITEM_POINTER.BEFORE.id,
        repeaterHooks.beforeGetRepeatedItemPointerHook
    )
    ps.extensionAPI.hooks.registerHook(COMPONENTS_HOOKS.DUPLICATE.COMPONENT.id, (event: ComponentDuplicateEvent) => {
        return component.duplicate(
            ps,
            event.compRefToAdd as CompRef,
            event.componentPointer as CompRef,
            event.newContainerPointer as CompRef,
            event.customId
        )
    })
    ps.extensionAPI.hooks.registerHook(
        COMPONENTS_METADATA_HOOKS.DERIVE_COMPONENT_METADATA.id,
        (event: DeriveComponentMetadataEvent) => {
            componentsMetaData.deriveMetaDataFrom(event.componentType, event.componentTypeToCopyTheMetadataFrom)
        }
    )
    syncBuilderComponentsMetadata(ps)
    registerComponentHooksFromExt(ps)
    hooks.registerHook(hooks.HOOKS.DATA.UPDATE_BEFORE, bridgeDataUpdateBeforeToExtension)
    /*should register after ext hooks - depends on convert mesh layout*/
    hooks.registerHook(hooks.HOOKS.ADD_ROOT.AFTER, convertCompToNewSiteWidthHook)
    ps.extensionAPI.nicknameContext.setContextProvider(null)
}
export default {
    initialize
}
